/** NOWPayments coins on the deposit screens: the order they are offered in, their names and icons. */
import { cryptoCoin, fmt, HIDDEN_CRYPTO_COINS, NETWORKS, type Network } from '@platform/shared';
import { COIN_MARKS } from './coinMarks';

/**
 * Order on the page: USDT on ERC20 first and preselected, then ETH, BTC and XRP, then the rest by
 * popularity. Within an asset its networks keep this order (USDT: ERC20, TRC20, BEP20, Solana …).
 * Coins not listed follow, A to Z.
 */
export const ORDER = [
  'usdterc20', 'usdttrc20', 'usdtbsc', 'usdtsol', 'usdtmatic',
  'eth', 'btc', 'xrp',
  'usdc', 'usdcerc20', 'usdcsol', 'usdcbsc', 'usdcmatic',
  'bnbbsc', 'sol', 'trx', 'doge', 'ada', 'ton', 'link', 'avaxc', 'ltc', 'bch', 'dot', 'xmr', 'arb', 'matic', 'eurt',
];
const rank = (c: string) => (ORDER.includes(c) ? ORDER.indexOf(c) : ORDER.length);
export const ordered = (list: string[]) =>
  list.filter((c) => !HIDDEN_CRYPTO_COINS.includes(c)).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
/** Shown until the server reports the coins selected in the NOWPayments dashboard. */
export const DEFAULT_COINS = ['usdterc20', 'usdttrc20', 'usdtbsc', 'usdtsol', 'eth', 'btc', 'usdc'];
/** How many assets head the picker as "Popular" (USDT, ETH, BTC, XRP). */
export const POPULAR_ASSETS = 4;

export const ASSET_NAME: Record<string, string> = {
  ETH: 'Ethereum', USDT: 'Tether', BTC: 'Bitcoin', XRP: 'Ripple', USDC: 'USD Coin', SOL: 'Solana', LTC: 'Litecoin', TRX: 'Tron', BNB: 'BNB',
  DOGE: 'Dogecoin', ADA: 'Cardano', TON: 'Toncoin', LINK: 'Chainlink', AVAX: 'Avalanche', BCH: 'Bitcoin Cash', DOT: 'Polkadot', XMR: 'Monero',
  ARB: 'Arbitrum', EURT: 'Euro Tether', MATIC: 'Polygon',
};

const MARKED = ['eth', 'btc', 'sol', 'ltc', 'trx', 'bnb', 'matic', 'xrp', 'xmr', 'ada', 'dot', 'link', 'avax', 'ton', 'bch', 'doge'];
/** Mark of the coin itself and, for tokens, of the network it travels on. */
export function marksOf(code: string): { coin: string | null; net: string | null } {
  const c = code.toLowerCase();
  const coin = c.startsWith('usdt') ? 'usdt' : c.startsWith('usdc') ? 'usdc' : (MARKED.find((k) => c.startsWith(k)) ?? null);
  const tail = c.replace(/^(usdt|usdc)/, '');
  const net = coin === 'usdt' || coin === 'usdc' ? ({ erc20: 'eth', '': 'eth', trc20: 'trx', bsc: 'bnb', sol: 'sol', matic: 'matic' } as Record<string, string>)[tail] ?? null : null;
  return { coin, net };
}

export function Mark({ k, size }: { k: string; size: number }) {
  const m = COIN_MARKS[k];
  if (!m) return null;
  return (
    <span className="grid shrink-0 place-items-center rounded-full text-white" style={{ width: size, height: size, background: m.color, padding: Math.round(size * 0.2) }} aria-hidden>
      <svg viewBox="0 0 24 24" className="size-full" fill="currentColor">
        <path d={m.path} />
      </svg>
    </span>
  );
}

/** Coin icon with the network as a small badge (USDT on Tron, …); a ticker disc for coins without a mark. */
export function CoinIcon({ code, size = 28, badge = true }: { code: string; size?: number; badge?: boolean }) {
  const { coin, net: n0 } = marksOf(code);
  const net = badge ? n0 : null;
  if (!coin || !COIN_MARKS[coin]) {
    const t = cryptoCoin(code).symbol.slice(0, 4);
    return (
      <span aria-hidden className="grid shrink-0 place-items-center rounded-full bg-pill font-bold text-fg" style={{ width: size, height: size, fontSize: Math.round(size * (t.length > 3 ? 0.26 : 0.32)) }}>
        {t}
      </span>
    );
  }
  return (
    <span className="relative inline-flex shrink-0">
      <Mark k={coin} size={size} />
      {net && (
        <span className="absolute -right-1 -bottom-1 rounded-full ring-2 ring-card">
          <Mark k={net} size={Math.round(size * 0.5)} />
        </span>
      )}
    </span>
  );
}

/** "USDT · Tron (TRC20)", or the ticker alone. */
export const coinLabel = (code: string) => {
  const c = cryptoCoin(code);
  return c.network ? `${c.symbol} · ${c.network}` : c.symbol;
};

/** Rough fee level of the chain a coin travels on, to help pick between networks of one asset. */
export function feeNote(code: string): string | null {
  const { coin, net } = marksOf(code);
  const chain = net ?? coin;
  if (chain === 'eth') return 'Higher network fee';
  if (chain && ['trx', 'bnb', 'sol', 'matic'].includes(chain)) return 'Low network fee';
  return null;
}

/** USDT on each withdrawal network, as the coin code the deposit icons know. */
const USDT_ON: Record<Network, string> = { TRC20: 'usdttrc20', ERC20: 'usdterc20', BEP20: 'usdtbsc' };
export const usdtIcon = (network: string, size = 30) => <CoinIcon code={USDT_ON[network as Network] ?? 'usdt'} size={size} />;
/** The withdrawal networks as dropdown options: name, fee and speed. */
export const networkOptions = () =>
  NETWORKS.map((n) => ({ id: n.id, icon: usdtIcon(n.id), title: `${n.chain} (${n.id})`, sub: n.eta }));

/** Icon of a crypto withdrawal: USDT with its network badge, BTC or ETH. */
export const cryptoIcon = (coin: string | null, network: string | null, size = 30) =>
  !coin || coin === 'USDT' ? usdtIcon(network ?? '', size) : <CoinIcon code={coin.toLowerCase()} size={size} />;
